import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import type {
  UserCompanyGroupDto,
  UserCompanyGroupWithMembersDto,
  PaginatedResultDto,
  CreateUserCompanyGroupDto,
  UpdateUserCompanyGroupDto,
} from '@monorepo/shared';
import { apiClient } from '../lib/axios';
import { queryClient } from '../lib/queryClient';

const groupsApi = {
  getCompanyGroups: async (
    companyId: string,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<PaginatedResultDto<UserCompanyGroupDto>> => {
    const params: Record<string, string | number> = { page, limit };
    if (search) params['search'] = search;

    const { data: response } = await apiClient.get<PaginatedResultDto<UserCompanyGroupDto>>(
      `/companies/${companyId}/groups`,
      { params },
    );
    return response;
  },

  getGroup: async (companyId: string, groupId: string): Promise<UserCompanyGroupWithMembersDto> => {
    const { data: response } = await apiClient.get<UserCompanyGroupWithMembersDto>(
      `/companies/${companyId}/groups/${groupId}`,
    );
    return response;
  },

  createGroup: async (
    companyId: string,
    data: CreateUserCompanyGroupDto,
  ): Promise<UserCompanyGroupDto> => {
    const { data: response } = await apiClient.post<UserCompanyGroupDto>(
      `/companies/${companyId}/groups`,
      data,
    );
    return response;
  },

  updateGroup: async (
    companyId: string,
    groupId: string,
    data: UpdateUserCompanyGroupDto,
  ): Promise<UserCompanyGroupDto> => {
    const { data: response } = await apiClient.put<UserCompanyGroupDto>(
      `/companies/${companyId}/groups/${groupId}`,
      data,
    );
    return response;
  },

  deleteGroup: async (companyId: string, groupId: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/groups/${groupId}`);
  },

  addMembersToGroup: async (
    companyId: string,
    groupId: string,
    memberIds: string[],
  ): Promise<UserCompanyGroupDto> => {
    const { data: response } = await apiClient.post<UserCompanyGroupDto>(
      `/companies/${companyId}/groups/${groupId}/members`,
      { memberIds },
    );
    return response;
  },

  removeMembersFromGroup: async (
    companyId: string,
    groupId: string,
    memberIds: string[],
  ): Promise<UserCompanyGroupDto> => {
    const { data: response } = await apiClient.delete<UserCompanyGroupDto>(
      `/companies/${companyId}/groups/${groupId}/members`,
      { data: { memberIds } },
    );
    return response;
  },
};

export const useGetCompanyGroups = (
  companyId: string,
  page: number = 1,
  limit: number = 10,
  search?: string,
) => {
  return useQuery({
    queryKey: ['company-groups', companyId, page, limit, search],
    queryFn: () => groupsApi.getCompanyGroups(companyId, page, limit, search),
    enabled: !!companyId,
  });
};

export const useGetGroup = (companyId: string, groupId: string) => {
  return useQuery({
    queryKey: ['company-group', companyId, groupId],
    queryFn: () => groupsApi.getGroup(companyId, groupId),
    enabled: !!companyId && !!groupId,
  });
};

const invalidateCompanyGroupsQueries = (companyId: string) => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === 'company-groups' &&
      query.queryKey[1] === companyId,
  });
};

const invalidateGroupMembershipQueries = (companyId: string) => {
  invalidateCompanyGroupsQueries(companyId);
  queryClient.invalidateQueries({ queryKey: ['company-group', companyId] });
  queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
};

export const useCreateGroup = (companyId: string) => {
  return useMutation({
    mutationFn: (data: CreateUserCompanyGroupDto) => groupsApi.createGroup(companyId, data),
    onSuccess: () => {
      invalidateCompanyGroupsQueries(companyId);
      toast.success(i18next.t('admin.groups.create_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useUpdateGroup = (companyId: string) => {
  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: UpdateUserCompanyGroupDto }) =>
      groupsApi.updateGroup(companyId, groupId, data),
    onSuccess: () => {
      invalidateGroupMembershipQueries(companyId);
      toast.success(i18next.t('admin.groups.update_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useDeleteGroup = (companyId: string) => {
  return useMutation({
    mutationFn: (groupId: string) => groupsApi.deleteGroup(companyId, groupId),
    onSuccess: () => {
      invalidateGroupMembershipQueries(companyId);
      toast.success(i18next.t('admin.groups.delete_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useAddMembersToGroup = (companyId: string) => {
  return useMutation({
    mutationFn: ({ groupId, memberIds }: { groupId: string; memberIds: string[] }) =>
      groupsApi.addMembersToGroup(companyId, groupId, memberIds),
    onSuccess: () => {
      invalidateGroupMembershipQueries(companyId);
      toast.success(i18next.t('admin.group_detail.add_members_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useRemoveMembersFromGroup = (companyId: string) => {
  return useMutation({
    mutationFn: ({ groupId, memberIds }: { groupId: string; memberIds: string[] }) =>
      groupsApi.removeMembersFromGroup(companyId, groupId, memberIds),
    onSuccess: () => {
      invalidateGroupMembershipQueries(companyId);
      toast.success(i18next.t('admin.group_detail.remove_members_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};
